import { Navigate, Route, Routes } from 'react-router-dom';

import AppLayout from '@/layout/AppLayout';

import ProtectedRoute from '@/router/ProtectedRoute';
import RoleBasedRedirect from '@/router/RoleBasedRedirect';
import ConsumerView from '@/views/consumer_view/ConsumerView';
import ErrorView from '@/views/error_view/ErrorView';
import SettingsView from '@/views/settings_view/SettingsView';
import SignInView from '@/views/sign-in_view/SignInView';

import useAuthWithRoutePreservation from '@/hooks/utils/useAuthWithRoutePreservation';
import { useOfflineDetection } from '@/hooks/utils/useOfflineDetection';

import useConfigStore from '@/store/configStore';

import { appRoutes } from '@/config/appRoutes';
import { COMPANY_TYPE } from '@/constants/company-type';
import { ROLES } from '@/constants/roles';
import { useSplashScreenDelay } from '@/hooks/utils/useSplashScreenDelay';
import { TradingSplashScreen } from '@trading-ui/custom';
import { useSessionStorage } from 'usehooks-ts';
import { STORAGE_KEYS } from '@/constants/storageKeys';

export default function RoutesComponent() {
  const isOnline = useOfflineDetection();
  const { environmentSettings } = useConfigStore();
  const { isLoggedIn, biofluxUser, isLoading } = useAuthWithRoutePreservation();
  const [hasLoggedOut] = useSessionStorage(STORAGE_KEYS.HAS_LOGGED_OUT, false);
  const { show: showSplashScreen } = useSplashScreenDelay(
    !biofluxUser || isLoading || !environmentSettings,
    environmentSettings !== null && !isLoading,
    3000
  );

  if (!isOnline) return <ErrorView errorCode="conexion-error" />;

  if (showSplashScreen && !hasLoggedOut) return <TradingSplashScreen open />;

  return (
    <Routes>
      <Route path="/" element={isLoggedIn && biofluxUser ? <AppLayout /> : <Navigate to={appRoutes.SIGN_IN} replace />}>
        <Route index element={<RoleBasedRedirect />} />
        <Route
          path={appRoutes.PRODUCER}
          element={
            <ProtectedRoute
              requiredRole={[ROLES.USER, ROLES.MANAGER, ROLES.MOEVE_ADMIN]}
              requiredCompanyType={COMPANY_TYPE.PRODUCER}>
              <div>Producer</div>
            </ProtectedRoute>
          }
        />
        <Route
          path={appRoutes.CONSUMER}
          element={
            <ProtectedRoute
              requiredRole={[ROLES.USER, ROLES.MANAGER, ROLES.MOEVE_ADMIN]}
              requiredCompanyType={COMPANY_TYPE.CONSUMER}>
              <ConsumerView />
            </ProtectedRoute>
          }
        />
        <Route
          path={appRoutes.SETTINGS}
          element={
            <ProtectedRoute requiredRole={[ROLES.MOEVE_ADMIN, ROLES.MANAGER]}>
              <SettingsView />
            </ProtectedRoute>
          }
        />
      </Route>
      <Route path={appRoutes.SIGN_IN} element={isLoggedIn ? <Navigate to="/" replace /> : <SignInView />} />
      <Route path={appRoutes.ERROR_403} element={<ErrorView errorCode={403} />} />
      <Route path={appRoutes.ERROR_401} element={<ErrorView errorCode={401} />} />
      <Route path={appRoutes.ERROR_404} element={<ErrorView errorCode={404} />} />
      <Route path="*" element={<Navigate to={isLoggedIn ? '/' : appRoutes.SIGN_IN} replace />} />
    </Routes>
  );
}
